import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getActivityLogs, exportActivityLogsCsv } from '../services/api';
import Navbar from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronLeft, ChevronRight, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_COLORS = {
  VENDOR_CREATED: 'bg-green-100 text-green-800',
  VENDOR_EDITED: 'bg-blue-100 text-blue-800',
  VENDOR_DELETED: 'bg-red-100 text-red-800',
  BANK_ADDED: 'bg-green-100 text-green-800',
  BANK_EDITED: 'bg-amber-100 text-amber-800',
  BANK_DELETED: 'bg-red-100 text-red-800',
  USER_CREATED: 'bg-green-100 text-green-800',
  USER_EDITED: 'bg-blue-100 text-blue-800',
  USER_DELETED: 'bg-red-100 text-red-800',
  PIN_SET: 'bg-purple-100 text-purple-800',
  RECHARGE_ADDED: 'bg-teal-100 text-teal-800',
  RECHARGE_DELETED: 'bg-red-100 text-red-800',
  VERIFICATION_UPDATE: 'bg-cyan-100 text-cyan-800',
  STATUS_AUTO_CHANGE: 'bg-indigo-100 text-indigo-800',
  PARTNER_CREATED: 'bg-emerald-100 text-emerald-800',
  PARTNER_DELETED: 'bg-red-100 text-red-800',
  PARTNER_ASSIGNED: 'bg-emerald-100 text-emerald-800',
};

const ACTION_LABELS = {
  VENDOR_CREATED: 'Vendor Created',
  VENDOR_EDITED: 'Vendor Edited',
  VENDOR_DELETED: 'Vendor Deleted',
  BANK_ADDED: 'Bank Added',
  BANK_EDITED: 'Bank Edited',
  BANK_DELETED: 'Bank Deleted',
  USER_CREATED: 'User Created',
  USER_EDITED: 'User Edited',
  USER_DELETED: 'User Deleted',
  PIN_SET: 'PIN Set',
  RECHARGE_ADDED: 'Recharge Added',
  RECHARGE_DELETED: 'Recharge Deleted',
  VERIFICATION_UPDATE: 'Verification',
  STATUS_AUTO_CHANGE: 'Auto Status Change',
  PARTNER_CREATED: 'Branch Created',
  PARTNER_DELETED: 'Branch Deleted',
  PARTNER_ASSIGNED: 'Branch Assigned',
};

const ITEMS_PER_PAGE = 25;

const ActivityLogsPage = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: '', entityType: '', vendorName: '', entityName: '' });
  const [vendorNames, setVendorNames] = useState([]);
  const [bankNames, setBankNames] = useState([]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: ITEMS_PER_PAGE,
        skip: page * ITEMS_PER_PAGE,
      };
      if (filters.action) params.action = filters.action;
      if (filters.entityType) params.entity_type = filters.entityType;
      if (filters.vendorName) params.vendor_name = filters.vendorName;
      if (filters.entityName) params.entity_name = filters.entityName;

      const response = await getActivityLogs(params);
      setLogs(response.data.logs);
      setTotal(response.data.total);
      if (response.data.vendorNames) setVendorNames(response.data.vendorNames);
      if (response.data.bankNames) setBankNames(response.data.bankNames);
    } catch (err) {
      console.error('Failed to fetch activity logs:', err.message);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const formatTimestamp = (ts) => {
    const d = new Date(ts);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = async () => {
    try {
      const params = {};
      if (filters.action) params.action = filters.action;
      if (filters.entityType) params.entity_type = filters.entityType;
      if (filters.vendorName) params.vendor_name = filters.vendorName;
      if (filters.entityName) params.entity_name = filters.entityName;
      const res = await exportActivityLogsCsv(params);
      downloadFile(res.data.csv, res.data.filename, 'text/csv');
      toast.success('CSV exported successfully');
    } catch (err) {
      console.error('CSV export failed:', err.message);
      toast.error('Failed to export CSV');
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = {};
      if (filters.action) params.action = filters.action;
      if (filters.entityType) params.entity_type = filters.entityType;
      if (filters.vendorName) params.vendor_name = filters.vendorName;
      if (filters.entityName) params.entity_name = filters.entityName;
      const res = await exportActivityLogsCsv(params);
      const rows = res.data.csv.split('\n');
      const header = rows[0];
      const dataRows = rows.slice(1).filter(r => r.trim());

      const sanitize = (str) => String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const htmlContent = `
        <html><head><title>Activity Logs Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #333; }
          h1 { font-size: 20px; margin-bottom: 4px; }
          .meta { color: #666; font-size: 12px; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background: #f1f5f9; font-weight: 600; text-align: left; padding: 8px; border: 1px solid #e2e8f0; }
          td { padding: 6px 8px; border: 1px solid #e2e8f0; vertical-align: top; }
          tr:nth-child(even) { background: #f8fafc; }
          @media print { body { padding: 12px; } }
        </style></head><body>
        <h1>Activity Logs Report</h1>
        <div class="meta">Generated: ${new Date().toLocaleString('en-IN')} | Total records: ${dataRows.length}</div>
        <table>
          <thead><tr>${header.split(',').map(h => `<th>${sanitize(h)}</th>`).join('')}</tr></thead>
          <tbody>${dataRows.map(row => {
            const cols = row.match(/(".*?"|[^,]*)(,|$)/g) || [];
            return `<tr>${cols.map(c => `<td>${sanitize(c.replace(/^"|"$/g, '').replace(/,$/,''))}</td>`).join('')}</tr>`;
          }).join('')}</tbody>
        </table></body></html>
      `;

      const printWindow = window.open('', '_blank');
      const doc = printWindow.document;
      doc.open();
      doc.write(htmlContent);
      doc.close();
      setTimeout(() => printWindow.print(), 500);
      toast.success('PDF ready for print');
    } catch (err) {
      console.error('PDF generation failed:', err.message);
      toast.error('Failed to generate PDF');
    }
  };

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" data-testid="logs-page-title">Activity Logs</h1>
            <p className="text-slate-600 mt-1">Track all actions performed across the system</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-1.5" data-testid="export-csv-btn">
              <Download className="w-4 h-4" /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF} className="gap-1.5" data-testid="export-pdf-btn">
              <FileText className="w-4 h-4" /> Print / PDF
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 items-end flex-wrap">
          <div className="w-48">
            <Select value={filters.action || 'all-actions'} onValueChange={(val) => { setFilters({ ...filters, action: val === 'all-actions' ? '' : val }); setPage(0); }}>
              <SelectTrigger data-testid="filter-action">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-actions">All Actions</SelectItem>
                <SelectItem value="VENDOR_CREATED">Vendor Created</SelectItem>
                <SelectItem value="VENDOR_EDITED">Vendor Edited</SelectItem>
                <SelectItem value="VENDOR_DELETED">Vendor Deleted</SelectItem>
                <SelectItem value="BANK_ADDED">Bank Added</SelectItem>
                <SelectItem value="BANK_EDITED">Bank Edited</SelectItem>
                <SelectItem value="BANK_DELETED">Bank Deleted</SelectItem>
                <SelectItem value="USER_CREATED">User Created</SelectItem>
                <SelectItem value="USER_EDITED">User Edited</SelectItem>
                <SelectItem value="USER_DELETED">User Deleted</SelectItem>
                <SelectItem value="PIN_SET">PIN Set</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Select value={filters.entityType || 'all-entities'} onValueChange={(val) => { setFilters({ ...filters, entityType: val === 'all-entities' ? '' : val }); setPage(0); }}>
              <SelectTrigger data-testid="filter-entity">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-entities">All Entities</SelectItem>
                <SelectItem value="Vendor">Vendor</SelectItem>
                <SelectItem value="Bank">Bank</SelectItem>
                <SelectItem value="User">User</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {vendorNames.filter(v => v && v.trim()).length > 0 && (
            <div className="w-48">
              <Select value={filters.vendorName || 'all-vendors'} onValueChange={(val) => { setFilters({ ...filters, vendorName: val === 'all-vendors' ? '' : val }); setPage(0); }}>
                <SelectTrigger data-testid="filter-vendor">
                  <SelectValue placeholder="All Vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-vendors">All Vendors</SelectItem>
                  {vendorNames.filter(v => v && v.trim()).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {bankNames.filter(b => b && b.trim()).length > 0 && (
            <div className="w-48">
              <Select value={filters.entityName || 'all-banks'} onValueChange={(val) => { setFilters({ ...filters, entityName: val === 'all-banks' ? '' : val }); setPage(0); }}>
                <SelectTrigger data-testid="filter-bank">
                  <SelectValue placeholder="All Banks" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-banks">All Banks</SelectItem>
                  {bankNames.filter(b => b && b.trim()).map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="text-sm text-slate-500">{total} logs found</div>
        </div>

        {/* Logs Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" id="logs-table-print">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 text-xs font-semibold text-slate-600">Timestamp</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-600">User</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-600">Action</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-600">Entity</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-600">Vendor</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-600">Changes</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" className="p-8 text-center text-slate-500">Loading...</td></tr>
                  ) : logs.length === 0 ? (
                    <tr><td colSpan="6" className="p-8 text-center text-slate-500">No activity logs found</td></tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id} className="border-b hover:bg-slate-50" data-testid={`log-row-${log.id}`}>
                        <td className="p-3 text-sm text-slate-600 whitespace-nowrap">{formatTimestamp(log.timestamp)}</td>
                        <td className="p-3">
                          <div className="text-sm font-medium">{log.userName}</div>
                          <div className="text-xs text-slate-500">{log.userRole}</div>
                        </td>
                        <td className="p-3">
                          <Badge className={ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-800'}>
                            {ACTION_LABELS[log.action] || log.action}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="text-sm font-medium">{log.entityName}</div>
                          <div className="text-xs text-slate-500">{log.entityType}</div>
                        </td>
                        <td className="p-3 text-sm text-slate-600">{log.vendorName || '-'}</td>
                        <td className="p-3">
                          {log.details && log.details.length > 0 ? (
                            <div className="space-y-1 max-w-md">
                              {log.details.map((d, idx) => (
                                <div key={`${d.field}-${idx}`} className="text-xs">
                                  <span className="font-medium text-slate-700">{d.field}:</span>{' '}
                                  {d.oldValue && <span className="text-red-600 line-through">{d.oldValue}</span>}
                                  {d.oldValue && d.newValue && ' → '}
                                  <span className="text-green-700">{d.newValue}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <div className="text-sm text-slate-500">
                  Page {page + 1} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default ActivityLogsPage;
